import axios, { getData, isAxiosError } from "../../../axios";
import { createAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as tPromise from "io-ts-promise";
import keyBy from "lodash/keyBy";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { notifyL } from "../../notifications";
import {
  AddDriver,
  addDriverT,
  UpdateDriver,
  updateDriverT,
} from "dora-contracts";
import {
  BaseDriverSchedule,
  Driver,
  DriverSchedule,
  driverSchedulesT,
  driversT,
  driverT,
} from "./types";
import { AppThunkAction } from "../../../redux-store";
import { loadTimeReservedForDrivers } from "../driver-time-reserved";
import { userStorage } from "../../../helpers/user-storage";

const prefix = "data/drivers";

// Saved per user and restored once the user is known (see checkAuthState).
export const DRIVERS_MANAGEMENT_TEAMS_FILTER =
  "driversManagementPageTeamsFilter";

interface State {
  driverEntities: Record<string, Driver>;
  driverIds: string[];
  status: "NOT_LOADED" | "LOADED" | "LOADING" | "LOAD_ERROR";
  filterTeamIds: string[];
  showCannotDeleteDriverWithRoutesDialog: boolean;
  showCannotArchiveDriverActiveRoutesDialog: boolean;
}

const initialState: State = {
  driverEntities: {},
  driverIds: [],
  status: "NOT_LOADED",
  filterTeamIds: [],
  showCannotDeleteDriverWithRoutesDialog: false,
  showCannotArchiveDriverActiveRoutesDialog: false,
};

export const addDriverSchedule = createErrorReportingAsyncThunk(
  `${prefix}/add-schedule`,
  async (input: { driverId: string; schedule: BaseDriverSchedule }) => {
    const { driverId, schedule } = input;
    return axios
      .post(`/api/drivers/${driverId}/schedules`, schedule)
      .then(getData)
      .then(tPromise.decode(driverSchedulesT));
  }
);

export const deleteDriverSchedule = createErrorReportingAsyncThunk(
  `${prefix}/delete-schedule`,
  async (input: { driverId: string; scheduleId: string }, { dispatch }) => {
    const { driverId, scheduleId } = input;
    return axios
      .delete(`/api/drivers/${driverId}/schedules/${scheduleId}`)
      .then(getData)
      .then(tPromise.decode(driverSchedulesT));
  }
);

export const loadDrivers = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (_: void, { dispatch }) => {
    const drivers = await axios
      .get("/api/drivers")
      .then(getData)
      .then(tPromise.decode(driversT));
    dispatch(loadTimeReservedForDrivers(drivers.map((driver) => driver.id)));
    return drivers;
  }
);

export const sendLoginSms = createErrorReportingAsyncThunk(
  `${prefix}/send-login-sms`,
  async (driverId: string) => {
    await axios.post(`/api/drivers/${driverId}/send-login-message`);
  }
);

export const addDriver = createErrorReportingAsyncThunk(
  `${prefix}/add-driver`,
  async (driver: AddDriver): Promise<Driver> => {
    return axios
      .post("/api/drivers", addDriverT.encode(driver))
      .then(getData)
      .then(tPromise.decode(driverT));
  }
);

export const updateDriver = createErrorReportingAsyncThunk(
  `${prefix}/update-driver`,
  async (driver: UpdateDriver & { id: string }): Promise<Driver> => {
    const { id, ...rest } = driver;
    return axios
      .put(`/api/drivers/${id}`, updateDriverT.encode(rest))
      .then(getData)
      .then(tPromise.decode(driverT));
  }
);

export const deleteDriver = createAsyncThunk(
  `${prefix}/delete-driver`,
  async (id: string, { dispatch }) => {
    try {
      await axios.delete(`/api/drivers/${id}`);
      return { success: true };
    } catch (err) {
      if (isAxiosError(err)) {
        if (err.response?.status === 409) {
          dispatch(slice.actions.openCannotDeleteDriverHasRoutesDialog());
          return { success: false };
        }
        dispatch(
          notifyL({
            namespace: "notifications",
            key: "driverCannotBeDeleted",
            type: "error",
          })
        );
      }
      throw err;
    }
  }
);

export const deactivateDriver = createErrorReportingAsyncThunk(
  `${prefix}/deactivate`,
  async (id: string, thunkAPI) => {
    try {
      const driver = await axios
        .post(`/api/drivers/${id}/deactivate`)
        .then(getData)
        .then(tPromise.decode(driverT));
      thunkAPI.dispatch(
        notifyL({
          namespace: "notifications",
          key: "driverArchived",
          type: "success",
        })
      );
      return driver;
    } catch (err) {
      if (isAxiosError(err)) {
        if (err.response?.status === 409) {
          thunkAPI.dispatch(
            slice.actions.openCannotArchiveDriverActiveRoutesDialog()
          );
          return null;
        }
        throw err;
      }
      throw err;
    }
  }
);

export const activateDriver = createErrorReportingAsyncThunk(
  `${prefix}/activate`,
  async (id: string, thunkAPI) => {
    const driver = await axios
      .post(`/api/drivers/${id}/activate`)
      .then(getData)
      .then(tPromise.decode(driverT));
    thunkAPI.dispatch(
      notifyL({
        namespace: "notifications",
        key: "driverReactivated",
        type: "success",
      })
    );
    return driver;
  }
);

export const toggleTeamFilterAndSave =
  (payload: string): AppThunkAction =>
  (dispatch: any, getState) => {
    dispatch(slice.actions.toggleTeamFilter(payload));
    const { filterTeamIds } = getState().data.drivers;
    userStorage.setItem(
      DRIVERS_MANAGEMENT_TEAMS_FILTER,
      JSON.stringify(filterTeamIds)
    );
  };

export const scheduleCreated = createAction<
  DriverSchedule & { driverId: string }
>(`${prefix}/schedule-created`);

export const scheduleDeleted = createAction<{
  driverId: string;
  scheduleId: string;
}>(`${prefix}/schedule-deleted`);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    toggleTeamFilter(state, action) {
      const id = action.payload;
      if (!state.filterTeamIds.includes(id)) {
        state.filterTeamIds.push(id);
      } else {
        state.filterTeamIds = state.filterTeamIds.filter(
          (teamId) => teamId !== id
        );
      }
    },
    setSelectedTeams(state, action) {
      state.filterTeamIds = action.payload;
    },
    openCannotDeleteDriverHasRoutesDialog(state) {
      state.showCannotDeleteDriverWithRoutesDialog = true;
    },
    closeCannotDeleteDriverHasRoutesDialog(state) {
      state.showCannotDeleteDriverWithRoutesDialog = false;
    },
    openCannotArchiveDriverActiveRoutesDialog(state) {
      state.showCannotArchiveDriverActiveRoutesDialog = true;
    },
    closeCannotArchiveDriverActiveRoutesDialog(state) {
      state.showCannotArchiveDriverActiveRoutesDialog = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(scheduleCreated, (state, action) => {
      const schedule = action.payload;
      // Socket events are broadcast org-wide; this tab may not have loaded
      // drivers (e.g. login page) or may not hold this driver yet.
      const driver = state.driverEntities[schedule.driverId];
      if (!driver) return;
      driver.schedules.push(schedule);
    });
    builder.addCase(scheduleDeleted, (state, action) => {
      const schedule = action.payload;
      const driver = state.driverEntities[schedule.driverId];
      if (!driver) return;
      driver.schedules = driver.schedules.filter(
        (x) => x.id !== schedule.scheduleId
      );
    });
    builder
      .addCase(addDriverSchedule.fulfilled, (state, action) => {
        const driverId = action.meta.arg.driverId;
        state.driverEntities[driverId].schedules = action.payload;
      })
      .addCase(deleteDriverSchedule.fulfilled, (state, action) => {
        const driverId = action.meta.arg.driverId;
        state.driverEntities[driverId].schedules = action.payload;
      })
      .addCase(loadDrivers.pending, (state) => {
        state.status = "LOADING";
      })
      .addCase(loadDrivers.fulfilled, (state, action) => {
        state.status = "LOADED";
        state.driverEntities = keyBy(action.payload, "id");
        state.driverIds = action.payload.map((x) => x.id);
      })
      .addCase(loadDrivers.rejected, (state) => {
        state.status = "LOAD_ERROR";
      })
      .addCase(addDriver.fulfilled, (state, action) => {
        const driver = action.payload;
        state.driverIds.push(driver.id);
        state.driverEntities[driver.id] = driver;
      })
      .addCase(updateDriver.fulfilled, (state, action) => {
        const driver = action.payload;
        state.driverEntities[driver.id] = driver;
      })
      .addCase(activateDriver.fulfilled, (state, action) => {
        const driver = action.payload;
        state.driverEntities[driver.id] = driver;
      })
      .addCase(deactivateDriver.fulfilled, (state, action) => {
        // if not conflict caught error (that still leads to .fulfilled)
        if (action.payload) {
          state.driverEntities[action.payload.id] = action.payload;
        }
      })
      .addCase(deactivateDriver.rejected, (state, action) => {
        if (action?.error?.message === "Request failed with status code 409") {
          state.showCannotArchiveDriverActiveRoutesDialog = true;
        }
      })
      .addCase(deleteDriver.fulfilled, (state, action) => {
        const result = action.payload.success;
        if (result) {
          const driverId = action.meta.arg;
          state.driverIds = state.driverIds.filter((x) => x !== driverId);
          delete state.driverEntities[driverId];
        }
      })
      .addCase(deleteDriver.rejected, (state, action) => {
        if (action?.error?.message === "Request failed with status code 409") {
          state.showCannotDeleteDriverWithRoutesDialog = true;
        }
      });
  },
});

export const {
  setSelectedTeams,
  closeCannotDeleteDriverHasRoutesDialog,
  closeCannotArchiveDriverActiveRoutesDialog,
} = slice.actions;

export default slice.reducer;
